import { useCallback, useEffect, useRef } from 'react'
import { useAppSelector } from '@/redux'
import { getCaptchaConfig, CaptchaConfigRes } from '@/api/user'

export type CaptchaResult = {
  ret: number
  ticket?: string
  randstr?: string
  appid?: string
  bizState?: any
  errorCode?: number | string
  errorMessage?: string
}

declare global {
  interface Window {
    TencentCaptcha?: any
  }
}

const loadScript = (src: string) => {
  const existed = document.querySelector(
    `script[src="${src}"]`,
  ) as HTMLScriptElement | null
  if (existed && (existed as any)._loaded) return Promise.resolve()
  return new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = () => {
      ;(s as any)._loaded = true
      resolve()
    }
    s.onerror = () => reject(new Error('TCaptcha script load failed'))
    document.head.appendChild(s)
  })
}

export const useTencentCaptcha = () => {
  const loadingRef = useRef<Promise<void> | null>(null)
  const ensureLoaded = useCallback(() => {
    if (!loadingRef.current) {
      loadingRef.current = loadScript(
        'https://turing.captcha.qcloud.com/TCaptcha.js',
      ).catch(() => loadScript('https://turing.captcha.gtimg.com/TCaptcha.js'))
    }
    return loadingRef.current
  }, [])

  useEffect(() => {
    ensureLoaded()
  }, [ensureLoaded])

  const verify = useCallback(
    async (callbacks: {
      onSuccess?: (payload: { ticket: string; randstr: string }) => void
      onClose?: () => void
      onError?: (error: { code?: number | string; message?: string }) => void
    }) => {
      const ctor = window.TencentCaptcha
      if (!ctor) {
        callbacks.onError?.({
          code: 'NoCtor',
          message: 'TencentCaptcha not available',
        })
        return
      }
      const cb = (res: CaptchaResult) => {
        if (res && res.ret === 0 && res.ticket && res.randstr) {
          callbacks.onSuccess?.({ ticket: res.ticket, randstr: res.randstr })
        } else if (res && res.ret === 2) {
          callbacks.onClose?.()
        } else {
          callbacks.onError?.({
            code: res?.errorCode,
            message: res?.errorMessage,
          })
        }
      }
      try {
        getCaptchaConfig().then((res: CaptchaConfigRes) => {
          const { captchaAppId, captchaAppIdEncrypted: aidEncrypted } = res
          const instance = new ctor(captchaAppId, cb, {
            lang: 'zh-cn',
            aidEncrypted,
          })
          instance.show()
        })
      } catch (e: any) {
        callbacks.onError?.({
          code: 'InitError',
          message: e?.message || String(e),
        })
      }
    },
    [ensureLoaded],
  )

  return verify
}
